<script lang="ts">
  import { fade } from "svelte/transition";
  import TextNew from "../TextNew.svelte";
  import { DEFAULT_FADE_TRANSITION } from "../modals/transitions";
  import { createModal, type ModalPartAttributes } from "../modals/createModal";
  import { createSuppliersByBuyerIDsQuery } from "../../queries/suppliers";
  import { derived, get } from "svelte/store";
  import Icon from "../icons/Icon.svelte";
  import { isPDF } from "../../helpers/file";
  import InvoiceUploadInstructions from "./InvoiceUploadInstructions.svelte";
  import ButtonNew from "../buttons/ButtonNew.svelte";
  import { createMutation } from "@tanstack/svelte-query";
  import { uploadPDFInvoices } from "../../helpers/mass-upload-api";
  import ShimmerOverlay from "../loaders/ShimmerOverlay.svelte";
  import { noop } from "../../helpers/utils";
  import InstructionalModal from "../modals/InstructionalModal.svelte";
  import { selectedBuyer } from "../../stores/buyer";
  import { createGetLandingQuery } from "../../helpers/landing";

  const landingQuery = createGetLandingQuery();

  const suppliersQuery = createSuppliersByBuyerIDsQuery(
    derived(landingQuery, ($landingQuery) => ({
      buyerIDs: $landingQuery.data?.buyers.map((b) => b.id) ?? [],
    }))
  );

  export let underlayAttrs: ModalPartAttributes;
  export let contentAttrs: ModalPartAttributes;
  export let open: boolean;
  export let onClose: () => void = noop;
  export let contentAction: ReturnType<typeof createModal>["content"];
  let isFileDraggedOverDropzone = false;
  let uploadableFileList: File[] = [];
  let failedFiles: File[] = [];

  $: if (open) {
    uploadableFileList = [];
    failedFiles = [];
  }

  const pdfUploadMutation = createMutation({
    mutationFn: (files: File[]) => {
      const buyer = get(selectedBuyer);
      if (!buyer) {
        throw new Error("No buyer selected");
      }
      const formData = new FormData();
      formData.append("buyer_id", buyer.id);
      files.forEach((file) => {
        formData.append("files", file);
      });
      return uploadPDFInvoices(formData);
    },
    onError: (_, files) => {
      failedFiles = [...failedFiles, ...files];
    },
    onSuccess(_, files) {
      failedFiles = failedFiles.filter(
        (f) => !files.some((file) => `${file.name}-${file.size}` === `${f.name}-${f.size}`)
      );
    },
  });

  function handleFileDrop(
    evt: DragEvent & {
      currentTarget: EventTarget & HTMLDivElement;
    }
  ) {
    evt.preventDefault();
    isFileDraggedOverDropzone = false;
    if ($pdfUploadMutation.isPending) {
      return;
    }
    if (!evt.dataTransfer?.items) {
      return;
    }
    const fileBatch: File[] = [];

    Array.from(evt.dataTransfer.items).forEach((item) => {
      if (item.kind !== "file") {
        return;
      }
      const file = item.getAsFile();
      if (!file || !isPDF(file)) {
        return;
      }
      if (uploadableFileList.some((f) => f.name === file.name && f.size === file.size)) {
        return;
      }

      fileBatch.push(file);
    });

    uploadableFileList = [...uploadableFileList, ...fileBatch];
    $pdfUploadMutation.mutate(fileBatch);
  }

  function handleRetry() {
    $pdfUploadMutation.mutate(failedFiles);
  }
</script>

<InstructionalModal
  {open}
  {underlayAttrs}
  {contentAttrs}
  {contentAction}
  backgroundImage="/static/assets/images/modal-bg-img.png"
>
  <div
    slot="leftPanel"
    class="modal-left"
    role="listbox"
    tabindex={0}
    on:drop={handleFileDrop}
    on:dragover={(evt) => {
      evt.preventDefault();
      isFileDraggedOverDropzone = true;
    }}
    on:dragleave={(evt) => {
      evt.preventDefault();
      isFileDraggedOverDropzone = false;
    }}
  >
    <div class="modal-left-content">
      {#if isFileDraggedOverDropzone}
        <div class="border" transition:fade|global={DEFAULT_FADE_TRANSITION}>
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 384 688"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="1"
              y="1"
              width={382}
              height={686}
              rx="7"
              stroke="white"
              stroke-width="2"
              stroke-dasharray="12 12"
              stroke-dashoffset={5}
            />
          </svg>
        </div>
      {/if}

      <div class="title">
        <TextNew weight="medium" size="xl" --rk-text-color="var(--rk-text-light)">
          {`follow 3 simple steps
            to send all your
            invoices to Insights`}
        </TextNew>
      </div>

      <div class="upload-details">
        <Icon name="upload" --w="32px" --h="32px" />
        <div class="upload-description">
          <TextNew size="s" --rk-text-color="var(--rk-text-low)" --whitespace="pre-line">
            {`you can also drag and drop
                original pdf invoices here`}
          </TextNew>
        </div>
      </div>

      {#if isFileDraggedOverDropzone}
        <div class="dropzone-overlay" transition:fade|global={DEFAULT_FADE_TRANSITION} />
        <div class="dropzone-overlay-content" transition:fade|global={DEFAULT_FADE_TRANSITION}>
          <div class="dropzone-image">
            <img
              src="/static/assets/images/document-illustration.png@2x.png"
              srcset="/static/assets/images/document-illustration.png, /static/assets/images/document-illustration@2x.png 2x"
              alt="drop document"
              width="66px"
            />
          </div>

          <TextNew size="l" --rk-text-color="var(--rk-text-light)">drop files here</TextNew>
        </div>
      {/if}
    </div>
  </div>

  <div
    slot="rightPanel"
    class="modal-right"
    style:overflow={!uploadableFileList.length ? "auto" : undefined}
  >
    {#if !uploadableFileList.length}
      <InvoiceUploadInstructions suppliers={$suppliersQuery.data?.data} />
    {:else}
      <div class="upload-panel">
        <div class="upload-close">
          <ButtonNew
            variant="icon"
            --icon-btn-pt="2px"
            --icon-btn-pb="2px"
            --icon-btn-pr="0px"
            --icon-btn-pl="0px"
            on:click={onClose}
          >
            <Icon slot="icon" name="close" --w="32px" --h="32px" />
          </ButtonNew>
        </div>
        <div class="upload-title-wrapper">
          <TextNew as="h2" size="l" weight="regular">manual upload</TextNew>
          {#if $pdfUploadMutation.isError}
            <TextNew as="p" size="s" --rk-text-color="var(--rekki-error)">
              something went wrong – please try again
            </TextNew>
          {:else}
            <TextNew as="p" size="s" --rk-text-color="var(--rk-text-low)">
              once processed, you'll see the uploaded files in the table
            </TextNew>
            <TextNew as="p" size="xs" --rk-text-color="var(--rk-text-low)"
              >expected turnaround: 1 working day</TextNew
            >
          {/if}
        </div>
        <div class="upload-list">
          {#each uploadableFileList as file}
            <div class="pdf-item">
              <TextNew size="s">
                {file.name}
              </TextNew>
            </div>
          {/each}
          {#if $pdfUploadMutation.isPending}
            <ShimmerOverlay />
          {/if}
        </div>
      </div>
      {#if $pdfUploadMutation.isSuccess || $pdfUploadMutation.isError}
        <div>
          <ButtonNew
            variant="primary"
            size="l"
            fullWidth
            on:click={$pdfUploadMutation.isError ? handleRetry : onClose}
          >
            <TextNew size="l" weight="regular">
              {$pdfUploadMutation.isError ? "retry" : "done"}
            </TextNew>
          </ButtonNew>
        </div>
      {/if}
    {/if}
  </div>
</InstructionalModal>

<style>
  .upload-list {
    position: relative;
    overflow: auto;
  }

  .pdf-item {
    border-top: solid 1px var(--rk-divider-0);
    padding: 12px 0px;
  }

  .modal-left {
    position: relative;
    flex: 0 1 416px;
    padding: 16px;
  }

  .modal-left-content {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .title {
    padding: 16px;
  }

  .upload-description {
    padding: 0px 8px 16px 8px;
  }

  .upload-close {
    position: absolute;
    top: 16px;
    right: 20px;
    color: var(--rk-neutral-2);
  }

  .upload-title-wrapper {
    padding: 7px 0px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .upload-panel {
    position: relative;
    padding: 16px 20px;
    background: var(--rk-primary-light);
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-radius: var(--rk-rounding-s);
    height: 100%;
  }

  .border {
    position: absolute;
    pointer-events: none;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }

  .modal-right {
    flex: 0 1 544px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 16px 16px 0px;
    height: 100%;
  }

  .modal-content-layout {
    display: flex;
    height: 100%;
  }

  .upload-details {
    color: var(--rk-text-transparency-light);
    opacity: 0.7;
    padding: 0px 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .dropzone-overlay {
    position: absolute;
    pointer-events: none;
    top: -16px;
    bottom: -16px;
    left: -16px;
    right: -16px;
    background: rgba(0, 0, 0, 0.65);
    border-radius: var(--rk-rounding-l);
  }

  .dropzone-overlay-content {
    position: absolute;
    pointer-events: none;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .dropzone-image {
    text-align: center;
    transform: rotate(-4deg);
    margin-bottom: 24px;
  }
</style>
